/**
 * The Settings "Notifications" section (#580, owner decision d-20260928-12): the signed-in member's
 * own category switches. A parent sees every category the server marks eligible, the payday
 * reminder included; a child sees only their own.
 *
 * **The server is the authority.** Rows come through `visiblePreferences`, which fails closed on a
 * row the server did not mark `eligible: true`, an unknown category, or a parent-only category for
 * anyone who is not a parent. A switch is NOT flipped before the server answers: it shows the value
 * the server last confirmed, is disabled while its save is in flight, and after a save shows the
 * value in the server's answer — never the value that was asked for. A refusal, a failure or an
 * answer that cannot be read leaves it where it was and says so.
 *
 * **One save at a time, for the whole section** (review r1 M1). There is one line that says what
 * happened, so while any switch saves, every switch is disabled. A save that outlives the list it
 * was made on — a new read began, or the section left the screen — changes nothing and says
 * nothing (review r1 M2 and L5), so the line always describes the list that is showing.
 *
 * **A parent's change needs the parent window** on the server (E25, review r1 H1). The client asks
 * once and replays once (`api/client.js` `request`); a check the parent does not finish is said so.
 *
 * One live region, and it persists, for the reason `Settings.jsx` gives beside the family switches:
 * a region added with its text already in place is not dependably spoken. The visible line under the
 * switches carries no role, so a screen reader hears each outcome once. That line hides itself with
 * `SCREEN_READER_ONLY` rather than relying on a page stylesheet (review r1 L3), so this line stays hidden
 * even without `Settings.jsx`. The section layout and retry link still use the Settings stylesheet.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import Switch from './Switch';
import { ApiError, PARENT_REAUTH_CODE } from '../api/client.js';
import { fetchNotificationPrefs, saveNotificationPref } from '../api/notificationPrefs.js';
import { visiblePreferences } from '../lib/notificationCategories.js';

export const PREFS_LOADING = 'Loading your notification settings…';
export const PREFS_UNREADABLE = "We couldn't load your notification settings, so they aren't shown. Nothing was changed.";
export const PREFS_NONE = 'There are no notifications for you to switch on or off here.';

/** Visually hidden, still read aloud. The same rule as `.set-sr` in `Settings.jsx`. */
const SCREEN_READER_ONLY = Object.freeze({
  position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden',
  clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', whiteSpace: 'nowrap', border: 0,
});

const onOff = (enabled) => (enabled ? 'on' : 'off');

function failedNote(row, error) {
  if (error instanceof ApiError && error.status === 403 && error.body?.code === PARENT_REAUTH_CODE) {
    return `${row.label} didn't change, because the parent check wasn't finished.`;
  }
  if (error instanceof ApiError && error.status === 403) return `The server refused to change ${row.label}. Nothing was changed.`;
  return `We couldn't confirm that ${row.label} saved. It shows the last setting we confirmed: ${onOff(row.enabled)}.`;
}

/** @param {{viewer: {role?: string}}} props the signed-in member; `role` decides nothing the server has not. */
export default function NotificationSettings({ viewer }) {
  const headingId = useId();
  const role = viewer?.role;
  const [load, setLoad] = useState({ status: 'loading', rows: [] });
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  // Checked synchronously, so two clicks inside one render cannot both send — on any two switches.
  const inFlight = useRef(false);
  // Which list is on screen. Bumped when a read starts and when the section unmounts; a save made
  // on an older list finds it changed and writes nothing.
  const generation = useRef(0);

  useEffect(() => {
    const gen = ++generation.current;
    const current = () => gen === generation.current;
    inFlight.current = false;
    setSaving(false);
    setLoad({ status: 'loading', rows: [] });
    setNote('');
    fetchNotificationPrefs().then(
      (answer) => { if (current()) setLoad({ status: 'ready', rows: visiblePreferences(answer, { role }) }); },
      () => { if (current()) setLoad({ status: 'error', rows: [] }); },
    );
    return () => { generation.current += 1; };
  }, [role, reloadKey]);

  const onChange = useCallback(async (row, next) => {
    if (inFlight.current) return;
    inFlight.current = true;
    const gen = generation.current;
    const current = () => gen === generation.current;
    setSaving(true);
    setNote(`Saving ${row.label}…`);
    try {
      const answer = await saveNotificationPref(row.category, next);
      if (!current()) return;
      const confirmed = visiblePreferences(answer, { role }).find((r) => r.category === row.category);
      if (!confirmed) {
        setNote(`We couldn't confirm that ${row.label} saved. It shows the last setting we confirmed: ${onOff(row.enabled)}.`);
        return;
      }
      setLoad((prev) => ({ ...prev, rows: prev.rows.map((r) => (r.category === row.category ? confirmed : r)) }));
      setNote(`${confirmed.label} is saved: ${onOff(confirmed.enabled)}`);
    } catch (error) {
      if (current()) setNote(failedNote(row, error));
    } finally {
      // A newer list already released the lock for itself when its read began.
      if (current()) {
        inFlight.current = false;
        setSaving(false);
      }
    }
  }, [role]);

  let standing = '';
  if (load.status === 'loading') standing = PREFS_LOADING;
  else if (load.status === 'error') standing = PREFS_UNREADABLE;
  else if (load.rows.length === 0) standing = PREFS_NONE;
  const line = note || standing;

  return (
    <section className="set-card" aria-labelledby={headingId} data-part="notifications">
      <div className="set-tile-h">
        <h2 id={headingId}>Notifications</h2>
        <span className="set-mono">just for you</span>
      </div>
      <p className="set-sr" style={SCREEN_READER_ONLY} role="status" aria-live="polite">{line}</p>
      {load.rows.map((row) => (
        <div className="set-row" key={row.category}>
          <Switch
            label={row.label}
            checked={row.enabled}
            disabled={saving}
            onCheckedChange={(next) => onChange(row, next)}
            showStateText
            onText="on"
            offText="off"
          />
        </div>
      ))}
      {line ? (
        <p className="desc">
          {line}
          {load.status === 'error' ? (
            <> <button type="button" className="set-link" onClick={() => setReloadKey((n) => n + 1)}>Try again</button></>
          ) : null}
        </p>
      ) : null}
    </section>
  );
}
